import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { UpsellPageProductSource } from "@redotech/redo-conversion-core-common/upsell-page/upsell-page-types";
import {
  PostPurchasePageStep,
  postPurchasePageStepSchema,
} from "@redotech/redo-flows-common/advanced-flow-db-parser";
import { t, useTranslation } from "@redotech/redo-i18n";
import AiStarIcon from "@redotech/redo-icons/ai-star-orange.svg";
import ArrowCircleUpRightIcon from "@redotech/redo-icons/arrow-circle-up-right.svg";
import Copy03Icon from "@redotech/redo-icons/copy-03.svg";
import PackageIcon from "@redotech/redo-icons/package.svg";
import { RedoConversionRpcClientContext } from "@redotech/redo-merchant-app-common/redo-conversion-rpc-client-provider";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { TextRedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { LoadingRedoAnimation } from "@redotech/redo-web/loading-redo-animation";
import { assertNever } from "@redotech/util/type";
import { memo, useMemo } from "react";
import { FrontendStepType, PartialWithRequiredType } from "..";
import {
  PostPurchasePageContent,
  ProductItemData,
} from "./post-purchase-page-content";

const PostPurchasePagePreview = memo(function PostPurchasePagePreview({
  step,
  setStep,
  readonly,
}: {
  step: PartialWithRequiredType<PostPurchasePageStep>;
  setStep: (step: PartialWithRequiredType<PostPurchasePageStep>) => void;
  readonly?: boolean;
}) {
  const { t } = useTranslation();
  const conversionRpcClient = useRequiredContext(
    RedoConversionRpcClientContext,
  );
  const merchantRpcClient = useRequiredContext(RedoMerchantRpcClientContext);

  const upsellPageLoad = useLoad(async () => {
    if (!step.upsellPageId) {
      return undefined;
    }
    const upsellPage = await merchantRpcClient.getUpsellPage({
      upsellPageId: step.upsellPageId,
    });

    return upsellPage;
  }, [step.upsellPageId]);

  const upsellPagesOptionsLoad = useLoad(async () => {
    const upsellPages = await conversionRpcClient.getUpsellPages(null);
    return upsellPages.map((upsellPage) => ({
      _id: upsellPage._id,
      name: upsellPage.name || t("Untitled"),
    }));
  }, []);

  const upsellPageOptions = useMemo<TextRedoListItem<string>[]>(() => {
    if (!upsellPagesOptionsLoad.value) {
      return [];
    }

    return upsellPagesOptionsLoad.value.map((upsellPage) => ({
      id: upsellPage._id,
      value: upsellPage._id,
      type: "text",
      text: upsellPage.name,
    }));
  }, [upsellPagesOptionsLoad.value]);

  const selectedOption = useMemo(
    () =>
      upsellPageOptions.find((option) => option.value === step.upsellPageId),
    [upsellPageOptions, step.upsellPageId],
  );

  const productItems: ProductItemData[] = useMemo(() => {
    if (!upsellPageLoad.value?.upsellPageData.layout.upsellProducts) {
      return [];
    }

    return upsellPageLoad.value.upsellPageData.layout.upsellProducts.map(
      (product) => {
        switch (product.upsellProductSource) {
          case UpsellPageProductSource.MANUAL: {
            const productInfo = upsellPageLoad.value?.productInfo?.get(
              product.productId,
            );

            const mainText = productInfo ? productInfo.name : product.productId;
            const imgSrc = productInfo ? productInfo.imageUrl : undefined;

            return imgSrc
              ? { mainText, imgSrc }
              : { mainText, Icon: PackageIcon };
          }
          case UpsellPageProductSource.SHOPIFY_RECOMMENDATION: {
            return {
              mainText: t("{{integrationName}} recommendation", {
                integrationName: "Shopify",
              }),
              Icon: ArrowCircleUpRightIcon,
            };
          }
          case UpsellPageProductSource.COLLECTION: {
            const collectionInfo = upsellPageLoad.value?.collectionInfo?.get(
              product.collectionId,
            );

            const mainText = collectionInfo
              ? collectionInfo.name
              : product.collectionId;

            return { mainText, Icon: Copy03Icon };
          }
          case UpsellPageProductSource.REDO_AI: {
            return {
              mainText: t("{{productName}} AI recommendation", {
                productName: "Redo",
              }),
              Icon: AiStarIcon,
            };
          }
          default: {
            assertNever(product);
          }
        }
      },
    );
  }, [
    upsellPageLoad.value?.upsellPageData.layout.upsellProducts,
    upsellPageLoad.value?.productInfo,
    upsellPageLoad.value?.collectionInfo,
    t,
  ]);

  const pending = upsellPageLoad.pending || upsellPagesOptionsLoad.pending;

  return (
    <Flex dir="column" gap="3xl" justify="flex-start">
      {pending ? (
        <LoadingRedoAnimation />
      ) : (
        <>
          {!readonly && !!upsellPagesOptionsLoad.value && (
            <RedoSingleSelectDropdownInput<string>
              label={t("Selected post-purchase page")}
              options={upsellPageOptions}
              optionSelected={({ item }) => {
                const selectedUpsellPage = upsellPagesOptionsLoad.value?.find(
                  (upsellPage) => upsellPage._id === item.value,
                );
                if (!selectedUpsellPage) {
                  return;
                }

                setStep({
                  ...step,
                  upsellPageId: selectedUpsellPage._id,
                  name: selectedUpsellPage.name,
                });
              }}
              selectedItem={selectedOption}
            />
          )}
          {step.upsellPageId && (
            <PostPurchasePageContent
              productItems={productItems}
              showName={!!readonly}
              upsellPageId={step.upsellPageId}
              upsellPageName={step.name || t("Untitled")}
            />
          )}
        </>
      )}
    </Flex>
  );
});

export const postPurchasePageStepType: FrontendStepType<
  PartialWithRequiredType<PostPurchasePageStep>
> = {
  type: "standard",
  valid: (step) => postPurchasePageStepSchema.safeParse(step).success,
  selectable: (_) => true,
  inspectorFactory: (props) => ({
    header: { title: t("Post-purchase page") },
    sections: [
      {
        fields: [
          {
            widget: "custom",
            Element: ({ value: step }) => (
              <PostPurchasePagePreview
                readonly={props.readonly}
                setStep={props.setStep}
                step={step}
              />
            ),
          },
        ],
      },
    ],
  }),
};
